import { injectIntl } from "react-intl";
import { useCallback, useEffect, useRef, useState } from "react";
import apiService from "src/services/api.service";

const parseHiddenTagIds = (response) => {
  const data = response?.data;

  if (Array.isArray(data?.tags)) {
    return data.tags.map((tag) => tag._id);
  }
  return [];
};

const PublicPageManagement = () => {
  const [allTags, setAllTags] = useState([]);
  const [hiddenTagIds, setHiddenTagIds] = useState([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [search, setSearch] = useState("");
  const dropdownRef = useRef(null);

  const loadData = useCallback(() => {
    Promise.all([apiService.getAllTags(), apiService.getPublicPageHiddenTags()])
      .then(([tagsResponse, hiddenResponse]) => {
        setAllTags(Array.isArray(tagsResponse?.data) ? tagsResponse.data : []);
        setHiddenTagIds(parseHiddenTagIds(hiddenResponse));
      })
      .catch(() => {
        setAllTags([]);
        setHiddenTagIds([]);
      });
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleClickOutside = useCallback((event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setDropdownOpen(false);
      setSearch("");
    }
  }, []);

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [handleClickOutside]);

  const saveHiddenTags = (tagIds) => {
    setHiddenTagIds(tagIds);
    apiService.updatePublicPageHiddenTags({ tagIds }).catch(() => loadData());
  };

  const addTag = (tag) => {
    if (hiddenTagIds.includes(tag._id)) {
      return;
    }
    saveHiddenTags([...hiddenTagIds, tag._id]);
    setDropdownOpen(false);
    setSearch("");
  };

  const removeTag = (tagId) => {
    saveHiddenTags(hiddenTagIds.filter((id) => id !== tagId));
  };

  const hiddenTags = allTags.filter((tag) => hiddenTagIds.includes(tag._id));

  const availableTags = allTags.filter(
    (tag) =>
      !hiddenTagIds.includes(tag._id) &&
      (tag.name || "").toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="PublicPageManagementContent">
      <div className="PublicPageVisibilitySection">
        <div className="PublicPageVisibilityIcon">
          <img
            alt="Globe"
            src="/media/icons/globe-02.svg"
            className="w-6 h-6"
          />
        </div>
        <div className="PublicPageVisibilityText">
          <div className="PublicPageVisibilityTitle">
            Public Page Visibility
          </div>
          <div className="PublicPageVisibilityDescription">
            Records carrying any of the tags below will be hidden from
            non-authenticated visitors.
          </div>
        </div>
      </div>

      <div className="PublicPageHiddenTagsSection">
        <div className="PublicPageHiddenTagsHeader">
          <div className="PublicPageHiddenTagsHeaderLeft">
            <div className="PublicPageHiddenTagsTitleRow">
              <span className="PublicPageHiddenTagsTitle">Hidden Tags</span>
              <span className="PublicPageHiddenTagsCount">
                {hiddenTags.length}
              </span>
            </div>
            <div className="PublicPageHiddenTagsDescription">
              Records tagged with any of these will not appear on the public
              page.
            </div>
          </div>
          <div className="PublicPageAddTagWrapper" ref={dropdownRef}>
            <button
              type="button"
              className="PrimaryButton"
              disabled={dropdownOpen}
              onClick={() => setDropdownOpen(true)}
            >
              <img
                alt="Add"
                src="/media/icons/plusIcon.svg"
                className="w-4 h-4"
              />
              Add Tag
            </button>
            {dropdownOpen && (
              <div className="PublicPageTagDropdown">
                <div className="PublicPageTagDropdownSearch">
                  <span className="PublicPageTagDropdownSearchIcon">
                    <img
                      alt="Search"
                      src="/media/icons/searchIcon.svg"
                      className="h-[14px] w-[14px]"
                    />
                  </span>
                  <input
                    type="text"
                    placeholder="Search tags..."
                    value={search}
                    className="PublicPageTagDropdownSearchInput"
                    onChange={(e) => setSearch(e.target.value)}
                    autoFocus
                  />
                </div>
                <ul className="PublicPageTagDropdownList">
                  {availableTags.map((tag) => (
                    <li key={tag._id}>
                      <button
                        type="button"
                        className="PublicPageTagDropdownItem"
                        onClick={() => addTag(tag)}
                      >
                        <img
                          alt="Tag"
                          src="/media/icons/tag.svg"
                          className="PublicPageTagDropdownItemIcon"
                        />
                        <span>{tag.name}</span>
                      </button>
                    </li>
                  ))}
                  {availableTags.length === 0 && (
                    <li className="PublicPageTagDropdownEmpty">
                      No tags found
                    </li>
                  )}
                </ul>
              </div>
            )}
          </div>
        </div>

        {hiddenTags.length > 0 && <div className="HorizontalDivider"></div>}

        {hiddenTags.length > 0 && (
          <div className="PublicPageHiddenTagsList">
            {hiddenTags.map((tag) => (
              <div key={tag._id} className="PublicPageHiddenTagChip">
                <span>{tag.name}</span>
                <button
                  type="button"
                  className="PublicPageHiddenTagRemove"
                  onClick={() => removeTag(tag._id)}
                  aria-label={`Remove ${tag.name}`}
                >
                  <img
                    alt="Remove"
                    src="/media/icons/closeIcon.svg"
                    className="w-[14px] h-[14px]"
                  />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default injectIntl(PublicPageManagement);
